"use client";

import type { CSSProperties, ReactNode } from "react";
import {
  ArrowRight,
  BadgeCheck,
  BellRing,
  CalendarClock,
  Check,
  Heart,
  Inbox,
  MessageSquareQuote,
  Search,
  ShieldCheck,
  Star,
  type LucideIcon,
} from "lucide-react";
import { StatusColorBadge } from "./BookingStatusBadge";
import { statusGuide } from "./bookingStatus";
import { Reveal } from "./Reveal";
import {
  HOW_IT_WORKS,
  type GuideDestination,
  type GuideFact,
  type GuideStep,
} from "./settingsCopy";

/**
 * Settings' How it works section (docs/ux/screens/ConsumerProfileScreen.md): the
 * whole product from the Consumer's side, as the five steps a booking lives
 * through, then the colours a booking wears and four things worth knowing.
 *
 * **Pure presentation.** It reads no data and calls nothing, so it renders
 * whatever state the account read is in, and every sentence comes from
 * `settingsCopy.ts`, which is what the apps port.
 *
 * **Motion explains the order** (§7): the hero line draws through the five
 * steps, then each step's rail grows down to the next as it scrolls in, its
 * tile pops onto the rail and its points arrive one at a time. All of it is
 * CSS keyed off `Reveal`'s `is-visible`, the hero's included: it sits below
 * the account sections, so an entrance on load would play where nobody can see
 * it. Each plays once, and is at rest under prefers-reduced-motion.
 *
 * Its headings start at `h3`, under the section's own `h2`.
 */

export type GuideLinkComponent = (props: {
  href: string;
  className?: string;
  children: ReactNode;
}) => ReactNode;

const STEP_ICON: Record<GuideStep["key"], LucideIcon> = {
  find: Search,
  request: CalendarClock,
  confirm: BadgeCheck,
  before: BellRing,
  after: Star,
};

const FACT_ICON: Record<GuideFact["key"], LucideIcon> = {
  saved: Heart,
  inbox: Inbox,
  reviews: MessageSquareQuote,
  privacy: ShieldCheck,
};

const HREF: Record<GuideDestination, string> = {
  search: "/search",
  bookings: "/bookings",
  care: "/care",
};

const delay = (ms: number) => ({ "--d": `${ms}ms` }) as CSSProperties;

function stepAnchor(key: GuideStep["key"]) {
  return `guide-step-${key}`;
}

export function HowItWorksGuide({ Link }: { Link: GuideLinkComponent }) {
  const steps: readonly GuideStep[] = HOW_IT_WORKS.steps;
  const total = steps.length;

  const jumpTo = (key: GuideStep["key"]) => {
    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    document
      .getElementById(stepAnchor(key))
      ?.scrollIntoView({ behavior: reduce ? "auto" : "smooth", block: "center" });
  };

  return (
    <div className="space-y-12">
      {/* The hero. Below the account sections, so it reveals on scroll. */}
      <Reveal
        as="section"
        className="relative overflow-hidden rounded-3xl bg-brand px-6 py-8 text-on-brand shadow-[var(--shadow-lift)] sm:px-10 sm:py-10"
      >
        <div className="mesh mesh-hero" aria-hidden />
        <div className="grid-lines opacity-60" aria-hidden />
        <div className="relative">
          <p className="text-xs font-bold uppercase tracking-[0.14em] text-teal">
            {HOW_IT_WORKS.eyebrow}
          </p>
          <h3 className="mt-2 text-2xl font-extrabold sm:text-3xl">
            {HOW_IT_WORKS.title}
          </h3>
          <p className="mt-2 max-w-lg text-sm text-on-brand/80 sm:text-base">
            {HOW_IT_WORKS.intro}
          </p>

          <nav aria-label="Steps" className="relative mt-8">
            <div
              aria-hidden
              className="guide-line absolute inset-x-5 top-5 h-0.5 rounded bg-linear-to-r from-teal via-teal/70 to-teal/30"
            />
            <ol className="relative flex justify-between">
              {steps.map((step, i) => {
                const Icon = STEP_ICON[step.key];
                return (
                  <li key={step.key} className="flex w-14 flex-col items-center sm:w-20">
                    <button
                      type="button"
                      onClick={() => jumpTo(step.key)}
                      aria-label={`${HOW_IT_WORKS.stepLabel(i + 1, total)}: ${step.title}`}
                      style={delay(350 + i * 120)}
                      className="guide-pop group flex size-10 items-center justify-center rounded-xl bg-brand ring-1 ring-teal/50 transition-colors duration-150 hover:bg-teal hover:text-brand"
                    >
                      <Icon
                        className="size-5 text-teal transition-colors duration-150 group-hover:text-brand"
                        strokeWidth={1.8}
                        aria-hidden
                      />
                    </button>
                    <span
                      style={delay(450 + i * 120)}
                      className="guide-pop mt-2 hidden text-center text-xs font-medium text-on-brand/75 sm:block"
                    >
                      {step.title}
                    </span>
                  </li>
                );
              })}
            </ol>
          </nav>
        </div>
      </Reveal>

      {/* The journey. */}
      <ol aria-label={HOW_IT_WORKS.title}>
        {steps.map((step, i) => (
          <Reveal as="li" key={step.key}>
            <GuideStepRow step={step} n={i + 1} total={total} Link={Link} />
          </Reveal>
        ))}
      </ol>

      {/* The four colours, explained by the same entries the badges use. */}
      <section aria-labelledby="guide-statuses">
        <Reveal>
          <h3 id="guide-statuses" className="text-xl font-bold text-ink">
            {HOW_IT_WORKS.statusesTitle}
          </h3>
          <p className="mt-1 text-sm text-muted">{HOW_IT_WORKS.statusesIntro}</p>
        </Reveal>
        <ul className="mt-5 grid gap-3 sm:grid-cols-2">
          {statusGuide().map((entry, i) => (
            // `.lift` sits inside the Reveal, never on it: both set
            // `transition`, and the later rule would drop the reveal's fade.
            <Reveal as="li" key={entry.key} delay={i * 80}>
              <div className="lift flex h-full gap-3 rounded-2xl border border-line bg-surface p-4 shadow-[var(--shadow-card)]">
                <StatusColorBadge tone={entry.tone} label={entry.label} size="lg" />
                <div className="min-w-0">
                  <p className="font-semibold text-ink">{entry.label}</p>
                  <p className="mt-0.5 text-sm text-body">{entry.meaning}</p>
                </div>
              </div>
            </Reveal>
          ))}
        </ul>
      </section>

      <section aria-labelledby="guide-facts">
        <Reveal>
          <h3 id="guide-facts" className="text-xl font-bold text-ink">
            {HOW_IT_WORKS.factsTitle}
          </h3>
        </Reveal>
        <ul className="mt-5 grid gap-3 sm:grid-cols-2">
          {HOW_IT_WORKS.facts.map((fact, i) => {
            const Icon = FACT_ICON[fact.key];
            return (
              <Reveal as="li" key={fact.key} delay={i * 80}>
                <div className="lift group h-full rounded-2xl border border-line bg-surface p-5 shadow-[var(--shadow-card)]">
                  <span className="flex size-10 items-center justify-center rounded-xl bg-accent/12 text-accent transition-transform duration-300 group-hover:-rotate-6 group-hover:scale-110">
                    <Icon className="size-5" strokeWidth={1.8} aria-hidden />
                  </span>
                  <p className="mt-3 font-semibold text-ink">{fact.title}</p>
                  <p className="mt-1 text-sm text-body">{fact.detail}</p>
                </div>
              </Reveal>
            );
          })}
        </ul>
      </section>

      <Reveal className="relative overflow-hidden rounded-3xl border border-line bg-surface px-6 py-8 text-center shadow-[var(--shadow-card)]">
        <div className="halo left-1/2 top-1/2 size-64 -translate-x-1/2 -translate-y-1/2 opacity-40" aria-hidden />
        <div className="relative">
          <p className="text-xl font-bold text-ink">{HOW_IT_WORKS.closingTitle}</p>
          <Link
            href={HREF[HOW_IT_WORKS.closingAction.to]}
            className="group mt-4 inline-flex min-h-11 items-center justify-center gap-2 rounded-lg bg-primary px-5 py-2.5 font-semibold text-on-primary transition hover:brightness-110"
          >
            {HOW_IT_WORKS.closingAction.label}
            <ArrowRight
              className="size-4 transition-transform duration-150 group-hover:translate-x-0.5"
              aria-hidden
            />
          </Link>
        </div>
      </Reveal>
    </div>
  );
}

function GuideStepRow({
  step,
  n,
  total,
  Link,
}: {
  step: GuideStep;
  n: number;
  total: number;
  Link: GuideLinkComponent;
}) {
  const Icon = STEP_ICON[step.key];
  const last = n === total;
  return (
    <div id={stepAnchor(step.key)} className="grid scroll-mt-24 grid-cols-[2.75rem_minmax(0,1fr)] gap-4">
      <div className="flex flex-col items-center" aria-hidden>
        <span className="guide-tile flex size-11 shrink-0 items-center justify-center rounded-xl bg-accent/12 text-accent ring-1 ring-accent/20">
          <Icon className="size-5" strokeWidth={1.8} />
        </span>
        {!last && (
          <span
            style={delay(250)}
            className="guide-rail mt-2 w-0.5 grow rounded bg-linear-to-b from-accent/50 to-line"
          />
        )}
      </div>

      <div
        className={`lift rounded-2xl border border-line bg-surface p-5 shadow-[var(--shadow-card)] ${last ? "" : "mb-6"}`}
      >
        <p className="text-xs font-bold uppercase tracking-[0.12em] text-accent">
          {HOW_IT_WORKS.stepLabel(n, total)}
        </p>
        <h4 className="mt-1 text-lg font-bold text-ink">{step.title}</h4>
        <p className="mt-1 text-sm text-body">{step.summary}</p>
        <ul className="mt-4 space-y-2">
          {step.points.map((point, i) => (
            <li
              key={point}
              style={delay(200 + i * 80)}
              className="guide-point flex gap-2.5 text-sm text-body"
            >
              <span className="mt-0.5 flex size-4.5 shrink-0 items-center justify-center rounded-md bg-accent/12 text-accent">
                <Check className="size-3" strokeWidth={3} aria-hidden />
              </span>
              <span>{point}</span>
            </li>
          ))}
        </ul>
        {step.action && (
          <Link
            href={HREF[step.action.to]}
            className="group mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-accent hover:underline"
          >
            {step.action.label}
            <ArrowRight
              className="size-4 transition-transform duration-150 group-hover:translate-x-0.5"
              aria-hidden
            />
          </Link>
        )}
      </div>
    </div>
  );
}
